'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface TimeSlot {
  time: string;
  available: boolean;
}

interface Expert {
  id: string;
  name: string;
  role: string;
  expertise: string[];
  image: string;
  alt: string;
}

interface ConsultationBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ConsultationBookingModal = ({ isOpen, onClose }: ConsultationBookingModalProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedExpert, setSelectedExpert] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    projectScope: '',
    preferredContact: 'email'
  });

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const experts: Expert[] = [
  {
    id: '1',
    name: 'Dr. Rajesh Kumar',
    role: 'AI Solutions Architect',
    expertise: ['AI Agents', 'Machine Learning', 'Cloud Architecture'],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fd29f585-1763300949045.png",
    alt: 'Professional headshot of South Asian man with glasses in navy blue suit smiling at camera'
  },
  {
    id: '2',
    name: 'Sarah Chen',
    role: 'Data Engineering Lead',
    expertise: ['Data Pipelines', 'Azure', 'Analytics'],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a2a4b7c2-1763296462144.png",
    alt: 'Professional portrait of Asian woman with long dark hair in white blouse against gray background'
  },
  {
    id: '3',
    name: 'Michael Thompson',
    role: 'Staffing Solutions Director',
    expertise: ['Contract Staffing', 'Team Scaling', 'Talent Acquisition'],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1630d3f34-1763292326902.png",
    alt: 'Professional headshot of Caucasian man with beard in charcoal suit smiling confidently'
  }];


  const timeSlots: TimeSlot[] = [
  { time: '09:00 AM', available: true },
  { time: '10:00 AM', available: true },
  { time: '11:00 AM', available: false },
  { time: '01:00 PM', available: true },
  { time: '02:00 PM', available: true },
  { time: '03:00 PM', available: true },
  { time: '04:00 PM', available: false }];


  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = () => {
    console.log('Consultation booked:', { ...formData, selectedDate, selectedTime, selectedExpert });
    onClose();
    setStep(1);
    setFormData({
      name: '',
      email: '',
      company: '',
      phone: '',
      projectScope: '',
      preferredContact: 'email'
    });
  };

  if (!isOpen) return null;

  if (!isHydrated) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
        <div className="bg-card rounded-lg p-8 max-w-2xl w-full mx-4">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-3/4" />
            <div className="h-4 bg-muted rounded w-1/2" />
            <div className="space-y-3">
              <div className="h-12 bg-muted rounded" />
              <div className="h-12 bg-muted rounded" />
              <div className="h-12 bg-muted rounded" />
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-lg shadow-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-headline font-bold text-foreground">
              Schedule Your Consultation
            </h2>
            <p className="text-sm text-muted-foreground font-body mt-1">
              Step {step} of 3
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-full transition-colors duration-base">

            <Icon name="XMarkIcon" size={24} variant="outline" />
          </button>
        </div>

        <div className="p-6">
          {step === 1 &&
          <div className="space-y-6">
              <div>
                <h3 className="text-lg font-headline font-semibold text-foreground mb-4">
                  Select Your Expert
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {experts.map((expert) =>
                <div
                  key={expert.id}
                  onClick={() => setSelectedExpert(expert.id)}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-base ${
                  selectedExpert === expert.id ?
                  'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`
                  }>

                      <div className="w-20 h-20 rounded-full overflow-hidden mx-auto mb-3">
                        <img
                      src={expert.image}
                      alt={expert.alt}
                      className="w-full h-full object-cover" />

                      </div>
                      <h4 className="text-center font-headline font-semibold text-foreground mb-1">
                        {expert.name}
                      </h4>
                      <p className="text-center text-sm text-muted-foreground font-body mb-3">
                        {expert.role}
                      </p>
                      <div className="flex flex-wrap gap-1 justify-center">
                        {expert.expertise.map((skill, idx) =>
                    <span
                      key={idx}
                      className="px-2 py-1 bg-muted text-xs font-body rounded-full text-muted-foreground">

                            {skill}
                          </span>
                    )}
                      </div>
                    </div>
                )}
                </div>
              </div>
              <button
              onClick={() => setStep(2)}
              disabled={!selectedExpert}
              className="w-full py-3 px-6 bg-primary text-primary-foreground rounded-md font-headline font-semibold hover:bg-secondary transition-colors duration-base disabled:opacity-50 disabled:cursor-not-allowed">

                Continue to Date & Time
              </button>
            </div>
          }

          {step === 2 &&
          <div className="space-y-6">
              <div>
                <h3 className="text-lg font-headline font-semibold text-foreground mb-4">
                  Choose Date & Time
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-body font-semibold text-foreground mb-2">
                      Select Date
                    </label>
                    <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary" />

                  </div>
                  <div>
                    <label className="block text-sm font-body font-semibold text-foreground mb-2">
                      Available Time Slots
                    </label>
                    <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                      {timeSlots.map((slot, idx) =>
                    <button
                      key={idx}
                      onClick={() => slot.available && setSelectedTime(slot.time)}
                      disabled={!slot.available}
                      className={`py-2 px-3 rounded-md text-sm font-body font-semibold transition-all duration-base ${
                      selectedTime === slot.time ?
                      'bg-primary text-primary-foreground' :
                      slot.available ?
                      'bg-muted text-foreground hover:bg-primary/10' :
                      'bg-muted/50 text-muted-foreground cursor-not-allowed'}`
                      }>

                          {slot.time}
                        </button>
                    )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                onClick={() => setStep(1)}
                className="flex-1 py-3 px-6 bg-muted text-foreground rounded-md font-headline font-semibold hover:bg-muted/80 transition-colors duration-base">

                  Back
                </button>
                <button
                onClick={() => setStep(3)}
                disabled={!selectedDate || !selectedTime}
                className="flex-1 py-3 px-6 bg-primary text-primary-foreground rounded-md font-headline font-semibold hover:bg-secondary transition-colors duration-base disabled:opacity-50 disabled:cursor-not-allowed">

                  Continue to Details
                </button>
              </div>
            </div>
          }

          {step === 3 &&
          <div className="space-y-6">
              <div>
                <h3 className="text-lg font-headline font-semibold text-foreground mb-4">
                  Your Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-body font-semibold text-foreground mb-2">
                      Full Name *
                    </label>
                    <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="John Doe" />

                  </div>
                  <div>
                    <label className="block text-sm font-body font-semibold text-foreground mb-2">
                      Email Address *
                    </label>
                    <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="john@company.com" />

                  </div>
                  <div>
                    <label className="block text-sm font-body font-semibold text-foreground mb-2">
                      Company Name *
                    </label>
                    <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Acme Corporation" />

                  </div>
                  <div>
                    <label className="block text-sm font-body font-semibold text-foreground mb-2">
                      Phone Number
                    </label>
                    <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="+1 (555) 123-4567" />

                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-body font-semibold text-foreground mb-2">
                      Project Scope
                    </label>
                    <textarea
                    name="projectScope"
                    value={formData.projectScope}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-3 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                    placeholder="Brief description of your project needs..." />

                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-body font-semibold text-foreground mb-2">
                      Preferred Contact Method
                    </label>
                    <select
                    name="preferredContact"
                    value={formData.preferredContact}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary">

                      <option value="email">Email</option>
                      <option value="phone">Phone</option>
                      <option value="video">Video Call</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                onClick={() => setStep(2)}
                className="flex-1 py-3 px-6 bg-muted text-foreground rounded-md font-headline font-semibold hover:bg-muted/80 transition-colors duration-base">

                  Back
                </button>
                <button
                onClick={handleSubmit}
                disabled={!formData.name || !formData.email || !formData.company}
                className="flex-1 py-3 px-6 bg-conversion text-conversion-foreground rounded-md font-headline font-semibold hover:bg-opacity-90 shadow-cta transition-all duration-base disabled:opacity-50 disabled:cursor-not-allowed">

                  Confirm Booking
                </button>
              </div>
            </div>
          }
        </div>
      </div>
    </div>);

};

export default ConsultationBookingModal;